/**
 * Created by Ning Jiang on 10/10/2018.
 */

import {GameConfig} from "@nolimitcity/slot-game/bin/core/gameconfig/GameConfig";
import {Reels} from "@nolimitcity/slot-game/bin/core/reel/reelarea/Reels";
import {IParsedStackedSymbolName, StateReelSymbol} from "@nolimitcity/slot-game/bin/core/reelsymbol/StateReelSymbol";
import {IBetLineWinData} from "@nolimitcity/slot-game/bin/core/server/data/IServerData";
import {SlotGame} from "@nolimitcity/slot-game/bin/core/SlotGame";
import {StageManager} from "@nolimitcity/slot-game/bin/core/stage/StageManager";
import {ArrayHelper} from "@nolimitcity/slot-game/bin/core/utils/ArrayHelper";
import {Helper} from "@nolimitcity/slot-game/bin/core/utils/Helper";
import {SequentiallyBetLineInitialWPController} from "@nolimitcity/slot-game/bin/game/winpresentation/initial/sequentially/SequentiallyBetLineInitialWPController";
import {Timeline, TimelineLite} from "gsap";
import {StarStruckParsedGameData} from "../../server/data/StarStruckParsedGameData";
import {StarStruckGameConfig} from "../../StarStruckGameConfig";
import {StarStruckSoundConfig} from "../../StarStruckSoundConfig";
import {StarStruckInitialWinCountUp} from "./StarStruckInitialWinCountUp";

export class StarStruckInitialWPController extends SequentiallyBetLineInitialWPController {

    private _reelsNum:number;
    private _symbolsNum:number;

    private _winCountUp:StarStruckInitialWinCountUp;
    private _symbolWinStates:boolean[][];
    private _belowStakeWinRestriction:boolean;

    constructor(indexes:[number, number]) {
        super(indexes);

        this._reelsNum = GameConfig.instance.REELS_NUM;
        this._symbolsNum = GameConfig.instance.SYMBOLS_NUM_IN_REEL[2];

        this._winCountUp = new StarStruckInitialWinCountUp();
        this._winCountUp.position.set(Helper.getSymbolPositions(1, 1)[0], Helper.getSymbolPositions(1, 1)[1]);
        StageManager.getLayer(StarStruckGameConfig.instance.LAYERS.WIN_COUNT_UP.name).addChild(this._winCountUp);
    }

    protected hasWin():boolean {
        return this._gameData!.totalWin > 0;
    }

    protected parseFeatureGameData(data:StarStruckParsedGameData):void {
        this._symbolWinStates = ArrayHelper.initArrayWithValues<boolean[]>(this._reelsNum, () => {
            return ArrayHelper.initArrayWithValues<boolean>(this._symbolsNum, () => false)
        });

        (<IBetLineWinData[]>this._betWinsData!).forEach((betLineWinData:IBetLineWinData) => {
            for(let reelId:number = 0; reelId < betLineWinData.nrOfSymbols; reelId++) {
                const symbolId:number = betLineWinData.betLine[reelId];
                this._symbolWinStates[reelId][symbolId] = true;

                if(GameConfig.instance.STACKED_SYMBOLS != null) {
                    // To mark all the stack win.
                    const parsedStackedSymbolName:IParsedStackedSymbolName = StateReelSymbol.parseStackedSymbolName(this._gameData!.reels[reelId][symbolId + GameConfig.instance.SYMBOLS_NUM_IN_REEL[1]], reelId);
                    if(parsedStackedSymbolName.isStacked) {
                        for(let i:number = 0; i < parsedStackedSymbolName.totalNum; i++) {
                            this._symbolWinStates[reelId][Math.min(GameConfig.instance.SYMBOLS_NUM_IN_REEL[2] - 1, Math.max(0, i - parsedStackedSymbolName.index + symbolId))] = true;
                        }
                    }
                }
            }
        });

        this._belowStakeWinRestriction = SlotGame.api.gameClientConfiguration.belowStakeWinRestriction && (data.singleWin <= data.playedBetValue);
    }

    protected startWinPresentation():void {
        super.startWinPresentation();

        const winSound:string | null = StarStruckSoundConfig.getNormalWinSoundName(this._gameData!.currentWinRatioConfig!);
        if(winSound && !this._belowStakeWinRestriction) {
            SlotGame.sound.playEffect(winSound);
        }
    }

    protected addSymbolWinAnimationToTimeline(betLineWinData:IBetLineWinData, timeline:Timeline, startTime:number, symbol:StateReelSymbol, reelId:number, symbolId:number):number {
        const symbolAnimation:Timeline = symbol.changeState({
            state : GameConfig.instance.SYMBOL_STATES.win,
            fadeInDuration : 0.1
        })!;

        timeline.add([
            symbolAnimation,
            () => {
                if (!this._belowStakeWinRestriction){
                    const winSound:string | null = StarStruckSoundConfig.instance.SYMBOL_WIN_HIT;
                    SlotGame.sound.playEffect(winSound);
                }
            }
        ], startTime);

        return this.getNextSymbolAnimationTimeOffset(symbolAnimation.totalDuration());
    }

    private addAllNoWinSymbolsToTimeline(timeline:Timeline, duration:number):void {
        let noWinSymbol:StateReelSymbol | null;
        for(let i:number = 0; i < this._symbolWinStates.length; i++) {
            for(let j:number = 0; j < this._symbolWinStates[i].length; j++) {
                if(this._symbolWinStates[i][j]) {
                    continue;
                }

                noWinSymbol = Reels.getSymbol(i, j);
                if(noWinSymbol == null) {
                    continue;
                }

                timeline.add(noWinSymbol.changeState({
                    state : GameConfig.instance.SYMBOL_STATES.noWin!,
                    fadeInDuration : duration
                }), 0);
            }
        }
    }

    protected playAllNoWinSymbols(timeline:Timeline):void {
        this.addAllNoWinSymbolsToTimeline(timeline, 1.5)
    }

    protected getNextSymbolAnimationTimeOffset(duration:number):number {
        return 0.2;
    }

    protected getNextBetWinAnimationTimeOffset(lastSymbolDelay:number):number {
        return 0.3;
    }

    protected addWinCountUpToTimeline(timeline:Timeline):void {
        if(this._gameData!.isBigWin) {
            return;
        }

        const totalWin:number = this._gameData!.totalWin;
        const from:number = totalWin - this._gameData!.singleWin;
        const duration:number = this._gameData!.currentWinRatioConfig!.duration;

        const countUpTimeline:TimelineLite = new TimelineLite();
        countUpTimeline.add(this._winCountUp.getCountUpAnimation(0, this._gameData!.singleWin, duration, this._gameData!.currentMultiplier));

        timeline.add(countUpTimeline, 0);
        const delay:number = countUpTimeline.duration();
        timeline.add(() => SlotGame.winFieldController.showWinField(totalWin), delay);
    }

    protected stopWinPresentation(isAborted:boolean):void {
        super.stopWinPresentation(isAborted);
        this._winCountUp.abort(this._gameData!.singleWin, isAborted, this._gameData!.currentMultiplier, this._gameData!.isBigWin);
        const stopTimeline:TimelineLite = new TimelineLite();
        // For abort before initial win start.
        this.addAllNoWinSymbolsToTimeline(stopTimeline, 0);
        SlotGame.winFieldController.showWinField(this._gameData!.totalWin);
    }
}